import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import {
  availabilityRedirectToDohop,
  availabilitySellTripSelection,
  availabilitySearchLowFareAvailabilitySuccess,
  availabilitySellTripSelectionSuccess,
  availabilitySearchAll,
  availabilitySearchFares,
  availabilitySearchFareAvailabilitySuccess,
  availabilitySearchLowFares,
  availabilitySearchAllHome,
  availabilitySearchAllModify,
  availabilitySellTripSelectionByQueryParams,
  AvailabilityActionTypes,
  availabilityGetRouteSchedule,
  availabilityGetRouteScheduleSuccess,
  availabilitySearchLowFaresMonthlyByOrigin,
  availabilitySearchLowFaresMonthlyByOriginSuccess,
  updateCalendarSearchCriteria,
  selectCalendarDate,
  availabilityStoreAvailabilitySelections,
  selectCalendarDay,
  availabilitySearchFromDestinations,
  availabilitySearchLowFaresSummary,
  availabilitySearchLowFaresSummarySuccess,
  availabilitySearchLowFaresMonthlyByDestination,
  availabilitySearchLowFaresMonthlyByDestinationSuccess,
  availabilitySearchFromDestinationsWithSchedule,
  availabilityGetBundlePrice, availabilityGetBundlePriceSuccess,
  availabilityConvertToOneWay,
  availabilityConvertToOneWaySuccess,
  availabilityConvertToOneWaySearch,
} from '@actions/availability.action';
import {
  selectAvailabilitySelectedTripsState,
  selectSearchInputState,
  selectAvailabilitySourceOrganization,
  selectAvailabilitySearchInputState, selectAvailabilitySelectedState,
} from '@selectors/availability.selector';
import { AvailabilityService } from '@services/api-clients';
import { TripService } from '@services/api-clients/trip.service';
import {
  N0SellKey,
  TripSellRequest,
} from '@models/requests/trip-sell-request.model';
import { EnvironmentService } from '@services/environment-service/environment.service';
import {
  createBookingSelection,
  getValidLowFareBeginDate,
  getValidLowFareEndDate, markRandomDatesAsCurrentlyViewed
} from '@utils/availability.utils';
import { BagsSsrEnum, PassengerTypeCode } from '@models/booking.models';
import { DateTime } from 'luxon';
import { DATE_FORMAT_YEAR_MONTH_DATE } from '@constants/date-format.constants';
import { selectBooking, selectEconomyJourneys, selectJourneys } from '@selectors/booking.selector';
import {
  selectCurrentCultureLanguageCode,
  selectSelectedCurrencyCode,
} from '@selectors/culture.selector';
import { DohopService } from '@services/do-hop/do-hop.service';
import {
  BookingActionTypes,
  bookingUpgradeCabin,
  upgradeToEconomyClassicActionSuccess
} from '@actions/booking.action';
import { FALLBACK_CURRENCY, routeDiscountMap } from '@constants/app.constants';
import { SearchRequest } from '@models/requests/search-request.model';
import { appNoOp, onError } from '@actions/app.action';
import { UtilitiesService } from '@services/utilities/utilities.service';
import { GrowthBookFeatureKeys } from '@models/growthbook.models';
import { GrowthBookService } from '@services/growthbook/growthbook.service';
import { bagsSellBagSsrsWithoutStepCompletion } from '@actions/bags.action';
import { selectAppFlowPathName, selectIsBookPath, selectIsModifyPath } from '@selectors/router.selector';
import { N0PassengerSsrsUpdate, N0SsrPassengerUpdateRequest, N0SsrUpdate } from '@models/requests/ssr-passenger-update-request.model';
import { CabinProductClass } from '@constants/cabin.constants';
import { BrowserService } from '@services/browser/browser.service';
import { BookingService } from "@services/api-clients/booking.service";
import { BaseError, ErrorType } from "@models/app.models";
import { TripType } from "@constants/home.constants";
import { getExtrasBundleCode } from "@utils/ssrs.utils";
import { SearchService } from 'src/app/features/search/services/search.service';

@Injectable()
export class AvailabilityEffect {
  environment = EnvironmentService.environment;
  readonly gbf = GrowthBookFeatureKeys;
  private gbs: GrowthBookService = inject(GrowthBookService);

  constructor(
    private actions$: Actions,
    private store: Store,
    private availabilityService: AvailabilityService,
    private tripService: TripService,
    private dohopService: DohopService,
    private bookingService: BookingService,
    private utilitiesService: UtilitiesService,
    private browserService: BrowserService,
    private searchService: SearchService,
  ) {}

  getAvailability$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        availabilitySearchAll,
        availabilitySearchFares,
        availabilitySearchFromDestinations,
        bookingUpgradeCabin,
      ),
      concatLatestFrom(() => [this.store.select(selectAppFlowPathName)]),
      mergeMap(([action, path]) =>
        this.availabilityService
          .getAvailability(
            {
              ...action.payload,
              passengers: action.payload.passengers?.filter(
                (p) => p.type !== PassengerTypeCode.INF,
              ),
            },
            path
          )
          .pipe(
            map((response) => {
              return availabilitySearchFareAvailabilitySuccess({
                  payload: response.data,
                  tripIndex: action.tripIndex,
                  clearBooking: !!action.clearBooking,
                  upgradeCabin: action.type === BookingActionTypes.upgradeCabin,
                  showStrikethroughPrice: this.is1472Enabled
                });
              }
            ),
          ),
      ),
    );
  });

  getAvailabilityConvertToOneWay$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        availabilityConvertToOneWay,
      ),
      concatLatestFrom(() => [
        this.store.select(selectAvailabilitySearchInputState),
        this.store.select(selectAppFlowPathName),
      ]),
      mergeMap(([_, availabilitySearchInput]) => {
        const payload = {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          ...availabilitySearchInput!,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          passengers: availabilitySearchInput!.passengers?.filter(
            (p) => p.type !== PassengerTypeCode.INF
          )
        } as SearchRequest;

        const isTravelersStart = this.gbs.growthBook.isOn(this.gbf.FEAT_US458_TRAVELERS_FIRST_IN_FLOW) &&
        ['b', 'c'].includes(
          this.gbs.growthBook.getFeatureValue(
            this.gbf.FEAT_US458_TRAVELERS_FIRST_IN_FLOW,
            '',
          ),
        );

        return of(availabilityConvertToOneWaySearch({ 
          payload,
          tripType: TripType.oneWay,
          clearBooking: false,
          isTravelersStart: isTravelersStart
        }));
      })
    );
  });

  getAvailabilityConvertToOneWaySearch$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        availabilityConvertToOneWaySearch,
      ),
      concatLatestFrom(() => [
        this.store.select(selectAvailabilitySearchInputState),
        this.store.select(selectAppFlowPathName),
      ]),
      mergeMap(([_, availabilitySearchInput, path]) => {
        const payload = {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          ...availabilitySearchInput!,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          passengers: availabilitySearchInput!.passengers?.filter(
            (p) => p.type !== PassengerTypeCode.INF
          )
        } as SearchRequest;

        return this.availabilityService
          .getAvailability(payload, path)
          .pipe(
            map((response) => {
              this.searchService.handleSkipReturn();
              return availabilityConvertToOneWaySuccess({
                payload: response.data,
                clearBooking: false,
                selectionOnTripConfirmationView: true,
                showStrikethroughPrice: this.is1472Enabled,
              });
            }),
          )
      }),
    );
  });

  getAvailabilityAndClearSearch$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        availabilitySearchAllHome,
      ),
      map((action) => {
          this.bookingService.deleteBookingInState().subscribe()
          catchError(() =>
            of(
              onError({error: new BaseError(ErrorType.BookingDeleteFailure)}),
            ),
          );
          return availabilitySearchFareAvailabilitySuccess({
            payload: null,
            tripIndex: action.tripIndex,
            clearBooking: true,
            showStrikethroughPrice: this.is1472Enabled,
          })
        }
      )
    )
  });

  getDateAvailability$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        selectCalendarDate
      ),
      concatLatestFrom(() => [
        this.store.select(selectAvailabilitySearchInputState),
        this.store.select(selectAppFlowPathName),
      ]),
      mergeMap(([_, availabilitySearchInput, path]) => {
        const payload = {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          ...availabilitySearchInput!,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          passengers: availabilitySearchInput!.passengers?.filter(
            (p) => p.type !== PassengerTypeCode.INF
          )
        } as SearchRequest;

        return this.availabilityService
          .getAvailability(payload, path)
          .pipe(
            map((response) => {
              const origin = payload.criteria[0]?.origin;
              const destination = payload.criteria[0]?.destination;
              let availabilityCopy = {...response.data};
              if (origin
                  && destination
                  && payload.criteria.length > 1
                  && this.is1472Enabled
              ) {
                const route = `${origin}-${destination}`
                const discount = routeDiscountMap[route] ?? 0.20;
                availabilityCopy = {
                  ...response.data,
                  results: response.data.results?.map((tripResult) => ({
                    ...tripResult,
                    trips: tripResult.trips.map((tripDateMarket) => ({
                      ...tripDateMarket,
                      journeysAvailableByMarket: tripDateMarket.journeysAvailableByMarket?.map((journey) => ({
                        ...journey,
                        fares: journey.fares.map((fare) => ({
                          ...fare,
                          details: {
                            ...fare.details,
                            totals: {
                              ...fare.details.totals,
                              roundedStrikethroughTotal: Math.ceil((fare.details.totals.publishedTotal / (1 - discount)) + fare.details.totals.taxesAndFeesTotal),
                            }
                          }
                        }))
                      }))
                    }))
                  }))
                };
              }

              return availabilitySearchFareAvailabilitySuccess({
                payload: availabilityCopy,
                clearBooking: false,
                showStrikethroughPrice: this.is1472Enabled,
              });
            })
          )
      }
      ),
    );
  });

  getDayAvailability$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        selectCalendarDay,
      ),
      tap(() => this.utilitiesService.updateSelectedTripIndex()),
      concatLatestFrom(() => [
        this.store.select(selectAvailabilitySearchInputState),
        this.store.select(selectAppFlowPathName),
      ]),
      filter(([action, availabilitySearchInput]) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return action.tripIndex === availabilitySearchInput!.criteria.length - 1
      }),
      mergeMap(([_, availabilitySearchInput, path]) => {
        const payload = {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          ...availabilitySearchInput!,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          passengers: availabilitySearchInput!.passengers?.filter(
            (p) => p.type !== PassengerTypeCode.INF
          )
        } as SearchRequest;

        return this.availabilityService
          .getAvailability(payload, path)
          .pipe(
            map((response) =>
              availabilitySearchFareAvailabilitySuccess({
                payload: response.data,
                clearBooking: false,
                selectionOnTripConfirmationView: true,
                showStrikethroughPrice: this.is1472Enabled,
              }),
            ),
          )
      }
      ),
    );
  });

  availabilitySearchFromDestinationsWithSchedule$: Observable<Action> = createEffect(() => {
    const isSeatsBeforeBags = this.gbs.isVariantOfExperimentEnabled('feat-002379-seatmap-PremiumUpgrade', 'b');
    return this.actions$.pipe(
      ofType(availabilitySearchFromDestinationsWithSchedule),
      mergeMap((action) => {
         return this.tripService.getSchedule({
            beginDate: action?.payload?.criteria[0]?.beginDate || '',
            destination: action?.payload?.criteria[0]?.destination || '',
            endDate: action?.payload?.criteria[0]?.endDate || '',
            origin: action?.payload?.criteria[0]?.origin || ''
          }).pipe(
            map((response) => {
              const payload = {
                payload: {
                  ...action.payload,
                  criteria: [
                    {
                      beginDate: response?.data?.[0]?.scheduleDay || new Date().toISOString(),
                      destination: action?.payload?.criteria[0]?.destination || '',
                      origin: action?.payload?.criteria[0]?.origin || '',
                    },
                    {
                      beginDate: response?.data?.[1]?.scheduleDay || new Date().toISOString(),
                      destination: action?.payload?.criteria[1]?.destination || '',
                      origin: action?.payload?.criteria[1]?.origin || '',
                    }
                  ],
                },
                tripType: TripType.roundTrip,
                clearBooking: true,
                isTravelersStart: true,
                isSeatsBeforeBags,
              };
                return availabilityGetRouteScheduleSuccess({
                  request: {
                    beginDate: action?.payload?.criteria[0]?.beginDate || '',
                    destination: action?.payload?.criteria[0]?.destination || '',
                    endDate: action?.payload?.criteria[0]?.endDate || '',
                    origin: action?.payload?.criteria[0]?.origin || ''
                  },
                  schedule: response.data,
                  fromDestination: true,
                  payload
                })
              }
            ),
          )
        }
      ),
    );
  });


  getLowFareAvailability$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        availabilitySearchAll,
        availabilitySearchAllHome,
        availabilitySearchAllModify,
        availabilitySearchLowFares,
        availabilitySearchFromDestinations,
        availabilityConvertToOneWaySearch,
      ),
      concatLatestFrom(() => [
        this.store.select(selectIsModifyPath),
        this.store.select(selectEconomyJourneys),
      ]),
      mergeMap(([action, isModifyPath, economyJourneys]) =>
        {
          const oneFareTypeEnabled = this.gbs.growthBook.isOn(GrowthBookFeatureKeys.FEAT_US581_SHOW_PREMIUM_WHEN_SOLD_OUT) && this.gbs.growthBook.getFeatureValue(GrowthBookFeatureKeys.FEAT_US581_SHOW_PREMIUM_WHEN_SOLD_OUT, '') === 'b';
          return this.availabilityService
            .getLowFareAvailability({
              ...action.payload,
              passengers: action.payload.passengers?.filter(
                (p) => p.type !== PassengerTypeCode.INF
              ),
              criteria: action.payload.criteria.map((c) => {
                const beginDt = getValidLowFareBeginDate(
                  c.beginDate,
                  false,
                  true
                );
                const endDt = getValidLowFareEndDate(
                  c.beginDate,
                  beginDt,
                  false,
                  true
                );
                return {
                  ...c,
                  beginDate: beginDt.toFormat(DATE_FORMAT_YEAR_MONTH_DATE),
                  endDate: endDt.toFormat(DATE_FORMAT_YEAR_MONTH_DATE),
                };
              }),
            }, !oneFareTypeEnabled || (isModifyPath && !economyJourneys?.length))
            .pipe(
              map((response) => {
                const updatedTrips = response.data.map(trip => ({
                  ...trip,
                  cabins: trip.cabins.map(cabin => ({
                    ...cabin,
                    lowFareAmounts: markRandomDatesAsCurrentlyViewed(cabin.lowFareAmounts)
                  }))
                }));
                return availabilitySearchLowFareAvailabilitySuccess({
                  payload: updatedTrips,
                  tripIndex: action.tripIndex,
                });
              })
            );
        },
      ),
    );
  });

  getLowFaresMonthlyByOrigin$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(availabilitySearchLowFaresMonthlyByOrigin),
      mergeMap((action) =>
        this.availabilityService.getLowFareMonthlyByOriginAvailability(action.payload).pipe(
          map((response) =>
            availabilitySearchLowFaresMonthlyByOriginSuccess({
              payload: response.data,
            }),
          ),
        ),
      ),
    );
  });

  getLowFaresMonthlyByDestination$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(availabilitySearchLowFaresMonthlyByDestination),
      mergeMap((action) =>
        this.availabilityService.getLowFareMonthlyByDestinationAvailability(action.payload).pipe(
          map((response) =>
            availabilitySearchLowFaresMonthlyByDestinationSuccess({
              payload: response.data,
            }),
          ),
        ),
      ),
    );
  });

  getLowFaresSummary$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(availabilitySearchLowFaresSummary),
      mergeMap((action) =>
        this.availabilityService.getLowFareSearchByOriginList(action.payload).pipe(
          map((response) =>
            availabilitySearchLowFaresSummarySuccess({
              payload: response.data,
            }),
          ),
        ),
      ),
    );
  });

  getCalendarAvailability$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        updateCalendarSearchCriteria
      ),
      concatLatestFrom(() => [
        this.store.select(selectSearchInputState),
        this.store.select(selectIsModifyPath),
        this.store.select(selectEconomyJourneys),
      ]),
      mergeMap(([_, searchInput, isModifyPath, economyJourneys]) => {
        const oneFareTypeEnabled = this.gbs.growthBook.isOn(GrowthBookFeatureKeys.FEAT_US581_SHOW_PREMIUM_WHEN_SOLD_OUT) && this.gbs.growthBook.getFeatureValue(GrowthBookFeatureKeys.FEAT_US581_SHOW_PREMIUM_WHEN_SOLD_OUT, '') === 'b';
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const payload = searchInput!;
          return this.availabilityService
          .getLowFareAvailability({
            ...payload,
            passengers: payload.passengers?.filter(
              (p) => p.type !== PassengerTypeCode.INF,
            )
          }, !oneFareTypeEnabled || (isModifyPath && (economyJourneys?.length ?? 0) === 0))
          .pipe(
            map((response) => {
              const updatedTrips = response.data.map(trip => ({
                ...trip,
                cabins: trip.cabins.map(cabin => ({
                  ...cabin,
                  lowFareAmounts: markRandomDatesAsCurrentlyViewed(cabin.lowFareAmounts)
                }))
              }));
              return availabilitySearchLowFareAvailabilitySuccess({
                payload: updatedTrips
              });
            }),
          )
        }
      ),
    );
  });

  getRouteSchedule$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(availabilityGetRouteSchedule),
      mergeMap((action) =>
        this.tripService.getSchedule(action.request).pipe(
          map((response) =>
            availabilityGetRouteScheduleSuccess({
              request: action.request,
              schedule: response.data,
            }),
          ),
        ),
      ),
    );
  });

  availabilityGetRouteScheduleSuccess$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(availabilityGetRouteScheduleSuccess),
      map((action) =>
        action.fromDestination && action.payload ?
          availabilitySearchFromDestinations(action.payload)
        : appNoOp()
      ),
    );
  });

  sellTripSelection$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        availabilitySellTripSelection,
        availabilitySellTripSelectionByQueryParams,
        upgradeToEconomyClassicActionSuccess,
      ),
      concatLatestFrom(() => [
        this.store.select(selectAvailabilitySelectedTripsState),
        this.store.select(selectSearchInputState),
        this.store.select(selectBooking),
        this.store.select(selectSelectedCurrencyCode),
        this.store.select(selectAvailabilitySourceOrganization),
        this.store.select(selectIsBookPath),
      ]),
      mergeMap(
        ([
          action,
          selectedTrips,
          searchInput,
          booking,
          currencyCode,
          sourceOrganization,
          isBookPath
        ]) => {
          const request: TripSellRequest = {
            firstFlightDate: searchInput?.criteria[0]?.beginDate,
            keys:
              selectedTrips?.filter<N0SellKey>(
                (t): t is N0SellKey => t !== null,
              ) ?? [],
            passengers: {
              types: (searchInput?.passengers ?? []).filter(
                (p) => p.type !== PassengerTypeCode.INF,
              ),
            },
            infantCount: (searchInput?.passengers ?? []).find(
              (p) => p.type === PassengerTypeCode.INF,
            )?.count,
            infantBirthDates: (searchInput?.passengers ?? [])
              .find((p) => p.type === PassengerTypeCode.INF)
              ?.dobs?.map((d) =>
                DateTime.fromISO(d).toFormat(DATE_FORMAT_YEAR_MONTH_DATE),
              ),
            childCount: (searchInput?.passengers ?? []).find(
              (p) => p.type === PassengerTypeCode.CHD,
            )?.count,
            childBirthDates: (searchInput?.passengers ?? [])
              .find((p) => p.type === PassengerTypeCode.CHD)
              ?.dobs?.map((d) =>
                DateTime.fromISO(d).toFormat(DATE_FORMAT_YEAR_MONTH_DATE),
              ),
            currencyCode:
              booking?.currencyCode ?? currencyCode ?? FALLBACK_CURRENCY,
            promotionCode: searchInput?.promotionCode,
            sourceOrganization,
          };
          const clearBooking = action.type ===
            AvailabilityActionTypes.sellTripSelectionByQueryParams 
            || (isBookPath && this.searchService.tripTypeChangedFromPreviousSearch);

          return this.tripService
            .sellTrip(
              request,
              clearBooking,
            )
            .pipe(
              map((response) => {
                const sellSuccessAction = availabilitySellTripSelectionSuccess({
                  payload: response.data,
                  isUpgraded: action.type === BookingActionTypes.upgradeToEconomyClassicActionSuccess,
                  redirect: !!action['redirect'],
                });

                if (isBookPath && this.gbs.growthBook.isOn(this.gbf.FEAT_US929_CB10_INCLUDED_WITH_ECONOMY_LIGHT) && this.gbs.growthBook.getFeatureValue(this.gbf.FEAT_US929_CB10_INCLUDED_WITH_ECONOMY_LIGHT, '') === 'b') {
                  const ssrUpdate = { ssrCode: BagsSsrEnum.CARRYON_BAG, count: 1 } as N0SsrUpdate;
                  const paxUpdate = Object.keys(response.data.passengers).map(k => ({ passengerKey: k, ssrs: [ssrUpdate]} as N0PassengerSsrsUpdate));
                  const journeyUpdate = response.data.journeys.filter(j => j.productClass === CabinProductClass.ECONOMY_LIGHT).map(j => ({
                    journeyKey: j.journeyKey,
                    passengers: paxUpdate
                  } as N0SsrPassengerUpdateRequest));

                  return bagsSellBagSsrsWithoutStepCompletion({ payload: journeyUpdate, next: sellSuccessAction });
                }
                return sellSuccessAction;
              }),
            );
        },
      ),
    );
  });

  resetSearchTripTypeChanged$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(availabilitySellTripSelectionSuccess),
        tap(() => {
          this.searchService.tripTypeChangedFromPreviousSearch = false;
        }),
      )
    },
    { dispatch: false },
  );

  redirectToDohop$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(availabilityRedirectToDohop),
        concatLatestFrom(() => [
          this.store.select(selectSelectedCurrencyCode),
          this.store.select(selectCurrentCultureLanguageCode),
        ]),
        tap(([action, currency, language]) => {
          this.browserService.open({
            url: this.dohopService.getDohopUrl(
              action.payload,
              action.tripType,
              currency ?? FALLBACK_CURRENCY,
              language,
              action.stations,
            ),
          });
        }),
      );
    },
    { dispatch: false },
  );

  createSelection$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        availabilitySearchFareAvailabilitySuccess
      ),
      concatLatestFrom(() => [
        this.store.select(selectJourneys),
      ]),
      map(([action, bookingJourneys]) => {
        if (!!action.upgradeCabin && bookingJourneys) {
          const { matchedJourneys, sellKeys } = createBookingSelection(action.payload?.results, bookingJourneys);
          return availabilityStoreAvailabilitySelections({
            journeys: matchedJourneys,
            sellKeys
          })
        }
        return appNoOp();
      })
    );
  });

  getBundle$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(availabilityGetBundlePrice),
      withLatestFrom(this.store.select(selectAvailabilitySelectedState)),
      mergeMap(([, selection]) => {
        if (!selection || !selection.sellKeys[0]?.bundleCode) {
          return of(appNoOp());
        }

        const bundle = getExtrasBundleCode(selection.sellKeys[0].bundleCode);

        if (!bundle) {
          return of(appNoOp());
        }

        return this.tripService
          .getBundlePrice(bundle)
          .pipe(
            map((response) => {
              return availabilityGetBundlePriceSuccess({price: response.data});
            })
          );
      }),
    );
  });

  get is1472Enabled () {
    return this.gbs.growthBook.isOn('feat-001472-round-trip-discount-alerts')
      && this.gbs.growthBook.getFeatureValue('feat-001472-round-trip-discount-alerts', '') === 'b';
  }
}
